import { Alert } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { apiWithoutCamelization } from "../../../../api";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import urls from "../../../../urls";
import { getErrorText } from "../../../../utils/get-error-text";
import type { ImportLogResponse } from "../../ThirdPartySystems.types";
import "./ImportLogs.scss";
import { ImportLogsTable } from "./ImportLogsTable/ImportLogsTable";

interface QueryOptions {
  limit: number;
  offset: number;
}

interface ImportLogsProps {
  configurationId: number;
  meterNames: Record<number, string>;
}

function ImportLogs({ configurationId, meterNames }: ImportLogsProps) {
  const [queryOptions, setQueryOptions] = useState<QueryOptions>({
    limit: 10,
    offset: 0
  });
  const { data, isError, error, isPending } = useQuery({
    queryKey: ["import-logs", { configurationId, queryOptions }],
    queryFn: () => fetchImportLogs(configurationId, queryOptions)
  });

  const handleFetchData = useCallback(
    (state: { page: number; pageSize: number }) => {
      const { page, pageSize } = state;
      const safePage = Math.floor(page);

      setQueryOptions(() => ({
        limit: pageSize,
        offset: safePage * pageSize
      }));
    },
    []
  );

  const logs = data?.results ?? [];
  const safeCount = data?.count ?? 0;

  return (
    <div className="ImportLogs">
      {isError ? (
        <Alert color={AlertPresets.error.color}>{getErrorText(error)} </Alert>
      ) : (
        <ImportLogsTable
          isLoading={isPending}
          logs={logs}
          meterNames={meterNames}
          numRows={safeCount}
          pageIndex={queryOptions.offset / queryOptions.limit}
          pageSize={queryOptions.limit}
          onFetchData={handleFetchData}
        />
      )}
    </div>
  );
}

async function fetchImportLogs(
  configurationId: number,
  queryOptions: QueryOptions
) {
  const response = await apiWithoutCamelization.get<ImportLogResponse>(
    urls.apiWithoutCamelization.subMeteringConfigurationImportLogs(
      configurationId,
      queryOptions
    )
  );

  return response.data;
}

export { ImportLogs, ImportLogsProps };
